// Seller subscription API (backend app/api/v1/subscription.py, plan
// 2026-11-01 part 3). Shared by the Wallet → Subscription page and the shell's
// warning strip.

import { apiUrl } from '../../lib/api'

export type SubscriptionCard = {
  stripe_payment_method_id: string
  brand: string | null
  last4: string | null
  exp_month: number | null
  exp_year: number | null
}

export type SubscriptionInvoice = {
  id: string
  number: string | null
  status: string
  amount_due_usd: string
  amount_paid_usd: string
  created_at: string
  hosted_invoice_url: string | null
}

export type SellerSubscription = {
  monthly_price_usd: string
  trial_days: number
  subscription_status: string | null
  subscribed_amount_usd: string | null
  trial_end: string | null
  current_period_end: string | null
  payment_failed_at: string | null
  restricted_after: string | null
  canceled_at: string | null
  card: SubscriptionCard | null
  saved_cards: SubscriptionCard[]
  invoices: SubscriptionInvoice[]
  stripe_unavailable: boolean
}

export type SellerSubscriptionStatus = {
  subscription_status: string | null
  trial_end: string | null
  payment_failed_at: string | null
  restricted_after: string | null
  has_saved_card: boolean
}

export const SUBSCRIPTION_QUERY = ['seller', 'subscription'] as const
export const SUBSCRIPTION_STATUS_QUERY = ['seller', 'subscription', 'status'] as const

export class SubscriptionApiError extends Error {
  readonly status: number
  readonly code: string | null

  constructor(message: string, status: number, code: string | null) {
    super(message)
    this.status = status
    this.code = code
  }
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(apiUrl(path), init)
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as
      | { detail?: { code?: string; message?: string } }
      | null
    throw new SubscriptionApiError(
      body?.detail?.message ?? `${path} failed: ${res.status}`,
      res.status,
      body?.detail?.code ?? null,
    )
  }
  return (await res.json()) as T
}

export const fetchSubscription = () => call<SellerSubscription>('/subscription')
export const fetchSubscriptionStatus = () =>
  call<SellerSubscriptionStatus>('/subscription/status')
export const startCardSetup = () =>
  call<{ client_secret: string; publishable_key: string | null }>('/subscription/card-setup', {
    method: 'POST',
  })
export const attachSubscriptionCard = (stripePaymentMethodId: string) =>
  call<SellerSubscription>('/subscription/card', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ stripe_payment_method_id: stripePaymentMethodId }),
  })

const LIVE = new Set(['trialing', 'active', 'past_due', 'unpaid', 'paused', 'incomplete'])

export function isLiveStatus(status: string | null): boolean {
  return status !== null && LIVE.has(status)
}
